import styled from '@emotion/styled';
import { FileReference } from '@kaizenplatform/belfast-entity';
import { FileIdsValue, SelectMultipleValue } from '@kaizenplatform/hugot-entity';
import { Typography } from '@material-ui/core';
import { ClassAttributes, createElement, Fragment } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  DynamicSchemaDefinitionField,
  DynamicSchemaDefinitionFileIds,
  DynamicSchemaDefinitionSection,
  ProjectDetailsForShowFragment,
} from '../../../generated/graphql';
import { filterBlankAssetCategorySectionsField } from '../../../graphql/dynamic_schema';
import { EnhancedBatchDownload } from '../../connected/EnhancedBatchDownload';
import { subText } from '../../constant/color';
import { SPACING_REGULAR, SPACING_UNIT } from '../../constant/spacing';
import { gridLayoutContainer, gridLayoutItem } from '../../styleUtility/gridLayout';
import mq from '../../styleUtility/mediaquery';
import Field from './AssetCategoryDetail/Field';

type Props = {
  className?: string;
  assetCategoryValue: {
    definition: ProjectDetailsForShowFragment['assetCategoryDefinition'];
    values: Map<string, string | number | boolean | SelectMultipleValue | FileIdsValue | null>;
  } | null;
  assetCategorySections: Array<
    Pick<DynamicSchemaDefinitionSection, 'id' | 'label' | 'meta'> & {
      fields: Array<Pick<DynamicSchemaDefinitionField, 'type' | 'key'> | Record<string, never>>;
    }
  > | null;
  assetFiles: ReadonlyArray<FileReference>;
};

const ProjectBasicSettingDetail = ({
  className,
  assetCategoryValue,
  assetCategorySections,
  assetFiles,
}: Props & ClassAttributes<HTMLElement>) => {
  const downloadFileIds =
    assetCategoryValue != null && assetCategorySections != null
      ? filterBlankAssetCategorySectionsField(assetCategorySections.flatMap((s) => s.fields))
          .filter((field): field is DynamicSchemaDefinitionFileIds => field.type === 'FILE_IDS')
          .flatMap((field) => assetCategoryValue.values.get(field.key) as ReadonlyArray<string>)
      : [];

  return (
    <Root className={className}>
      <Section>
        <SectionHeading variant="h6">
          <FormattedMessage id="project_basic_setting_detail.section_heading.assets" />
        </SectionHeading>

        <LabelAndValue>
          <Label>
            <FormattedMessage id="project_basic_setting_detail.assets.label.uploaded_files" />
          </Label>
          <Value>
            {assetCategorySections != null &&
              assetCategoryValue != null &&
              assetCategorySections.map((section) => {
                return (
                  <Fragment key={section.id}>
                    <Typography variant="h6">{section.label}</Typography>
                    {filterBlankAssetCategorySectionsField(section.fields).map((field) => (
                      <Field
                        key={field.key}
                        field={field}
                        value={assetCategoryValue.values}
                        files={assetFiles}
                        meta={section.meta}
                      />
                    ))}
                  </Fragment>
                );
              })}

            {downloadFileIds.length >= 2 ? <EnhancedBatchDownload fileIds={downloadFileIds} /> : null}
          </Value>
        </LabelAndValue>
      </Section>
    </Root>
  );
};

const Root = styled('div')({});

const Section = styled('div')({
  marginBottom: SPACING_REGULAR,
  [mq('sp')]: {
    marginBottom: SPACING_UNIT * 3,
    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
    '&:last-child': {
      marginBottom: 0,
      borderBottom: 'none',
    },
  },
});

const SectionHeading = styled(Typography)({
  marginBottom: SPACING_REGULAR,
  [mq('sp')]: {
    marginBottom: SPACING_UNIT * 3,
  },
});
SectionHeading.defaultProps = { variant: 'h6' };

export const LabelAndValue = styled('div')({
  ...gridLayoutContainer({
    areas: [['label', '.', 'value']],
    columns: ['20%', `${SPACING_UNIT * 3}px`, `calc(100% - 20% - ${SPACING_UNIT * 3}px)`],
    rows: ['auto'],
  }),
  marginBottom: SPACING_UNIT,
  [mq('sp')]: {
    ...gridLayoutContainer({
      areas: [['label'], ['.'], ['value']],
      columns: ['1fr'],
      rows: ['auto', 'auto', 'auto'],
    }),
    marginBottom: SPACING_UNIT * 3,
  },
});

export const Label = styled(Typography)({
  ...gridLayoutItem('label'),
  textAlign: 'end',

  [mq('sp')]: {
    textAlign: 'start',
    fontSize: 12,
    fontWeight: 400,
    marginBottom: 4,
    color: subText,
  },
});

export const Value = styled('div')({
  ...gridLayoutItem('value'),
});

export const ValueText = styled(Typography)({
  wordBreak: 'break-word' as any,
  hyphens: 'auto',
});

export default ProjectBasicSettingDetail;
